import * as d3 from 'd3'
import DeskLady from 'assets/images/desklady.svg'

import Topic from 'models/Topic'

interface Wobble {
    x: number
    y: number
}

interface D3TopicSelectorProps {
    topics: Topic[]
    width: number
    height: number
    colorPrimary: string
    colorBackground: string
    colorText: string
    onSelectTopic: (topicId: number) => void  
}

class D3TopicSelector {  
    containerEl: HTMLElement
    props: D3TopicSelectorProps
    svg: any

    constructor(containerEl: HTMLElement, props: D3TopicSelectorProps) {
        const { width, height } = props

        this.containerEl = containerEl
        this.props = props

        this.svg = d3.select(containerEl)
            .append('svg')
            .style('background-color', 'transparent')
            .attr('width', width)
            .attr('height', height)
        this.updateItems()
    }

    radians = (degrees: number) => degrees * Math.PI / 180

    updateItems = () => {
        const { svg, props: { topics, width, colorPrimary, colorBackground, colorText } } = this
    
        const angle = 180 / (topics.length - 1)
        const centerImageWidth = 1160 / 2
        const centerImageHeight = 992 / 2
        const whiteDotR = 90
        const iconSize = 56
        const circleRX = centerImageWidth / 2 + whiteDotR + 25
        const circleRY = centerImageWidth / 2 + whiteDotR + 50
        const cx = width / 2
        const cy = circleRY + iconSize/2

        const objX: number[] = []
        const objY: number[] = []

        const svgWidth = (circleRX + whiteDotR) * 2 + 32
        let svgHeight = cy * 1.5

        const g = svg.append("g")

        // Scale svg if it execeed the view width
        if (svgWidth > width) {
            const scale = width / svgWidth
            const placedX = cx - circleRX
            let translateX = 0
            if (placedX < whiteDotR) {
                translateX = (whiteDotR - placedX + 25) * scale
            }
            g.attr("transform", `translate(${translateX}, 0) scale(${scale}, ${scale})`)
            svgHeight = svgHeight * scale
        }

        // Calc location of each topic bubble 
        for (let index=0; index < topics.length; index++) {
            // console.log(cx + circleRX * Math.cos(this.radians(180 + index * angle)))
            objX.push(cx + circleRX * Math.cos(this.radians(180 + index * angle)))
            objY.push(cy + whiteDotR + circleRY * Math.sin(this.radians(180 + index * angle)))
        }

        g.selectAll("image")
            .data([0])
            .enter()
            .append('image')
            .attr("xlink:href", DeskLady)           
            .attr('x', cx - centerImageWidth/2)
            .attr('y', cy - centerImageHeight/2)
            .attr('width', centerImageWidth)
            .attr('height', centerImageHeight)
        
        g.selectAll('g')
            .data(topics)
            .enter()
            .append('g')
            .on('mouseenter', (topic: Topic, index: number, nodes: HTMLElement[]) => this.onMouseEnter(topic, nodes[index], colorPrimary))
            .on('mouseleave', (topic: Topic, index: number, nodes: HTMLElement[]) => this.onMouseLeave(topic, nodes[index], colorBackground))
            .on('mousedown', (topic: Topic, index: number, nodes: HTMLElement[]) => this.onMouseDown(topic, nodes[index], colorPrimary))
            .on('mouseup', (topic: Topic, index: number, nodes: HTMLElement[]) => this.onMouseUp(topic, nodes[index], colorBackground))
            .call((p: any) => {
                p
                    .append('circle')
                    .style('fill', colorBackground)
                    .attr('cx', (_: Topic, index: number) => objX[index])
                    .attr('cy', (_: Topic, index: number) => objY[index])
                    .attr('r', whiteDotR)
                p
                    .append('image')
                    .attr("xlink:href", (topic: Topic, _: number) => topic.image)
                    .attr('x', (_: Topic, index: number) => objX[index] - iconSize / 2)
                    .attr('y', (_: Topic, index: number) => objY[index] - whiteDotR - iconSize / 2)
                    .attr('width', iconSize)
                    .attr('height', iconSize)

                p
                    .append('foreignObject')
                    .attr('x', (_: Topic, index: number) => objX[index] - whiteDotR*0.8)
                    .attr('y', (_: Topic, index: number) => objY[index] - whiteDotR*0.3)
                    .attr("width", whiteDotR * 1.6)
                    .attr("height", whiteDotR)
                    .append("xhtml:body")
                    .style("font-size", '1.3rem')
                    .style('color', colorText)
                    .style('background-color', 'transparent')
                    .style('text-align', 'center')
                    .html((topic: Topic) => topic.name)
            })

        svg
            .attr('height', svgHeight)  // resize to fit svg height
    }

    onMouseEnter = (topic: Topic, node: HTMLElement, color: string) => {
        // console.log("onMouseEnter", node)
        d3.select(node).select('circle').style("fill", "#eeeeee")
    }

    onMouseLeave = (topic: Topic, node: HTMLElement, color: string) => {
        // console.log("onMouseLeave", node)
        d3.select(node).select('circle').style("fill", color).style("stroke", "none")
    }

    onMouseDown = (topic: Topic, node: HTMLElement, color: string) => {
        // console.log("onMouseDown")
        d3.select(node).select('circle').style("fill", "#eeeeee").style("stroke", color).style("stroke-width", 4)
    }

    onMouseUp = (topic: Topic, node: HTMLElement, color: string) => {
        console.log("Selected topic:", topic.name)
        this.props.onSelectTopic(topic.id)
    }

    resize = (width: number, height:number) => {
        // console.log("Resize", width, height)
        const { svg } = this
        
        this.props.width = width
        this.props.height = height

        svg
            .attr('width', width)
            .attr('height', height)

        svg.selectAll("*").remove()
        this.updateItems()
    }

}

export default D3TopicSelector
